import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

type Props = {
  conceptName?: string;
  /**
   * upi_score is expected in [0..1]
   */
  upiScore?: number;
  gainToday?: number;
};

const GAUGE_DASH_ARRAY = 251.2;

export const Learnometer = ({ conceptName, upiScore, gainToday }: Props) => {
  const learnometerValue = (upiScore || 0) * 100;
  const gainValue = Number(((gainToday || 0) * 100).toFixed(2));
  const clampedLearnometerValue = Math.min(100, Math.max(0, learnometerValue));
  const learnometerDashOffset =
    GAUGE_DASH_ARRAY * (1 - clampedLearnometerValue / 100);

  return (
    <Paper
      sx={{
        bgcolor: 'white',
        p: 2,
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: 1.25 }}
      >
        <Typography sx={{ fontSize: 24 }}>⭐</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
          Learnometer
        </Typography>
      </Stack>

      {/* Concept badge with correct gradient */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 1.75,
          py: 1,
          mb: 1.5,
          background: 'linear-gradient(135deg, #f0f4ff, #e8f0fe)',
          borderRadius: 2,
          border: '1px solid rgba(99,102,241,0.15)',
        }}
      >
        <Typography sx={{ fontSize: 14 }}>📚</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#4f46e5' }}>
          {conceptName}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <svg
          viewBox="0 0 200 120"
          width="100%"
          style={{
            maxWidth: 220,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.08))',
          }}
        >
          <defs>
            <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop
                offset="0%"
                style={{ stopColor: '#ef4444', stopOpacity: 1 }}
              />
              <stop
                offset="33%"
                style={{ stopColor: '#f59e0b', stopOpacity: 1 }}
              />
              <stop
                offset="66%"
                style={{ stopColor: '#fbbf24', stopOpacity: 1 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: '#22c55e', stopOpacity: 1 }}
              />
            </linearGradient>
          </defs>
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#e8e8e8"
            strokeWidth="18"
            strokeLinecap="round"
          />
          {clampedLearnometerValue > 0 && (
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="url(#gaugeGrad)"
              strokeWidth="18"
              strokeLinecap="round"
              strokeDasharray={GAUGE_DASH_ARRAY}
              strokeDashoffset={learnometerDashOffset}
            />
          )}
          <text
            x="100"
            y="85"
            textAnchor="middle"
            style={{
              fontSize: 36,
              fontWeight: 800,
              fill: '#333',
              fontFamily: 'inherit',
            }}
          >
            {Math.round(clampedLearnometerValue)}
          </text>
          <text
            x="100"
            y="105"
            textAnchor="middle"
            style={{
              fontSize: 11,
              fontWeight: 500,
              fill: '#999',
              fontFamily: 'inherit',
            }}
          >
            out of 100
          </text>
        </svg>
        {!!gainValue && (
          <Chip
            label={`${gainValue > 0 ? '+' : ''}${Math.round(
              gainValue
            )} points today`}
            size="small"
            sx={{
              mt: 0.5,
              background: 'linear-gradient(135deg, #d4edda, #c3e6cb)',
              color: '#155724',
              fontWeight: 600,
              fontSize: 13,
            }}
          />
        )}
      </Box>
    </Paper>
  );
};
